<template>
    <div class="relative border border-gray-300 bg-gray-warm-50 rounded-md px-6 py-5">
        <h2 class="text-gray-400 text-lg font-semibold pb-2">
            Signers
        </h2>

        <div v-if="editable">
            <div v-if="property.broker"
                class="flex items-start pt-4 pb-6 pl-8 mb-3 mt-3 border-y border-gray-300"
            >
                <envelope-recipient-broker
                    :property="property"
                    :envelope="envelope"
                    :broker_will_sign="broker_will_sign_proxy"
                />

                <div class="flex flex-col justify-center items-center ml-2 w-18">
                    <label for="broker_will_sign"
                        class="font-semibold text-sm text-center text-gray-500 leading-none pb-3"
                        @click="broker_will_sign_proxy = ! broker_will_sign_proxy"
                    >
                        Broker <br>will sign?
                    </label>

                    <input
                        name="broker_will_sign"
                        type="checkbox"
                        v-model="broker_will_sign_proxy"
                        class="w-4 h-4"
                    />
                </div>
            </div>

            <p v-if="! recipients_proxy.length"
                class="text-base text-gray-500 mb-3"
            >
                Specify one or more individuals who must sign the documents in this Envelope.
            </p>

            <div v-else>
                <div v-for="(recipient, index) in recipients_proxy"
                    :key="recipient.id"
                    class="flex items-start pt-1 pb-6 mb-3 pl-8 border-b border-gray-300"
                >
                    <envelope-recipient
                        :index="index"
                        :recipient="recipient"
                    />

                    <div v-if="recipient.completed_at"
                        class="flex-none ml-2 w-18"
                    ></div>

                    <div v-else
                        class="cursor-pointer flex flex-none justify-center hover:text-red-600 items-center text-gray-400 ml-2 w-18 mt-1 pt-10"
                        @click="removeRecipient(recipient)"
                    >
                        <i class="fas fa-minus-circle"></i>
                    </div>
                </div>
            </div>
        </div>

        <div v-else
            class="mt-1"
        >
            <p v-if="! broker_will_sign_proxy && ! recipients_proxy.length"
                class="text-base text-gray-500 mb-3"
            >
                There are no signers for this Envelope
            </p>

            <div v-if="broker_will_sign_proxy && property.broker" class="flex items-center">
                <div class="text-md font-semibold"
                    :class="envelope.broker_completed_at ? 'text-blue-600' : 'text-black'"
                >
                    <i class="text-sm fas fa-user mb-2 mr-1"
                        :class="envelope.broker_completed_at ? 'text-blue-400' : 'text-gray-300'"
                    />

                    {{ property.broker.name }}
                </div>

                <div class="text-md mx-2 text-gray-400">
                    /
                </div>

                <div class="text-md"
                    :class="envelope.broker_completed_at ? 'text-blue-600' : 'text-black'"
                >
                    <i class="text-sm fas fa-envelope mb-2 mr-1"
                        :class="envelope.broker_completed_at ? 'text-blue-400' : 'text-gray-300'"
                    />

                    {{ property.broker.email }}
                </div>
            </div>

            <div v-for="recipient in recipients_proxy"
                class="flex-1 flex justify-between pt-1.5"
            >
                <div class="flex items-center">
                    <div class="text-md font-semibold"
                        :class="recipient.completed_at ? 'text-blue-600' : 'text-black'"
                    >
                        <i class="text-sm fas fa-user mb-2 mr-1"
                            :class="recipient.completed_at ? 'text-blue-400' : 'text-gray-300'"
                        />

                        {{ recipient.name }}
                    </div>

                    <div class="text-md mx-2 text-gray-400">
                        /
                    </div>

                    <div class="text-md"
                        :class="recipient.completed_at ? 'text-blue-600' : 'text-black'"
                    >
                        <i class="text-sm fas fa-envelope mb-2 mr-1"
                            :class="recipient.completed_at ? 'text-blue-400' : 'text-gray-300'"
                        />

                        {{ recipient.email }}
                    </div>
                </div>

                <div v-if="recipient.completed_at"
                    class="text-sm text-blue-600 pl-8"
                >
                    Signed on <span class="font-bold">{{ formatDate(recipient.completed_at) }}</span>
                    at <span class="font-bold">{{ formatTime(recipient.completed_at) }}</span>
                </div>
            </div>
        </div>

        <div v-if="editable"
            class="w-full flex justify-end"
            :class="recipients_proxy.length ? 'pt-2' : ''"
        >
            <button-blue-outline
                size="small"
                @click="addRecipient"
            >
                <i class="fa fa-plus text-2xs -ml-1 mr-1"></i> {{ recipients_proxy.length ? $t('Add another signer') : $t('Add a signer') }}
            </button-blue-outline>
        </div>

        <div class="absolute top-0 right-0">
            <toast-message :message="toast" />
        </div>
    </div>
</template>

<script>
import ButtonBlueOutline from '../../../components/button-blue-outline.vue';
import EnvelopeRecipient from './envelope-recipient.vue';
import EnvelopeRecipientBroker from './envelope-recipient-broker.vue';
import ToastMessage from '../../../components/toast-message.vue';

export default {
    components: {
        ButtonBlueOutline,
        EnvelopeRecipient,
        EnvelopeRecipientBroker,
        ToastMessage,
    },

    props: {
        property: {},
        envelope: {},
        broker_will_sign: {},
        statuses: { default: () => [] },
        toast: {},
    },

    data() {
        return {
            recipients_proxy: this.envelope?.recipients || [],
            broker_will_sign_proxy: this.broker_will_sign,
        }
    },

    computed: {
        editable() {
            return ! this.envelope?.sent_at;
        },
        has_recipients() {
            return !! this.recipients_proxy.filter(recipient => recipient.name || recipient.email).length;
        },
        recipients_are_valid() {
            return this.has_recipients
                && this.recipients_proxy.every(recipient => recipient.name && this.emailIsValid(recipient.email));
        },
    },

    methods: {
        addRecipient() {
            this.recipients_proxy.push({
                id: this.generateRandomId(),
                name: null,
                email: null,
            })
        },
        removeRecipient(recipient_to_remove) {
            this.recipients_proxy.splice(
                this.recipients_proxy.findIndex(recipient => recipient.id == recipient_to_remove.id), 1
            );
        },
        generateRandomId() {
            return Math.floor(Math.random() * (9999999 - 1) + 1);
        },
        formatDate(timestamp) {
            return timestamp ? dayjs(timestamp).format('M/D/YYYY') : null;
        },
        formatTime(timestamp) {
            return timestamp ? dayjs(timestamp).format('h:mm A') : null;
        },
        updateRecipientStatuses(statuses) {
            statuses.forEach(status => {
                let recipient_index = this.recipients_proxy.findIndex(proxy => proxy.id === status.id);

                if (recipient_index > -1) {
                    this.recipients_proxy[recipient_index].status = status.status;
                    this.recipients_proxy[recipient_index].sent_at = status.sent_at;
                    this.recipients_proxy[recipient_index].completed_at = status.completed_at;
                }
            });
        },
    },

    created() {
        this.updateRecipientStatuses(this.statuses);
    },

    watch: {
        recipients_proxy: {
            deep: true,
            handler(value) {
                this.$emit('recipientsUpdated', value);
            },
        },
        broker_will_sign_proxy: {
            handler(value) {
                this.$emit('brokerWillSignUpdated', value);
            },
        },
        statuses: {
            deep: true,
            handler(value) {
                this.updateRecipientStatuses(value);
            },
        },
    },
}
</script>
